import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { styled } from '@mui/material/styles';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { parseCSV, processProjectionData, deleteProjection } from '../utils/projectionUtils';
import ProjectionDetails from '../components/ProjectionDetails';
import ProjectionEstimator from '../components/ProjectionEstimator';
import DeleteIcon from '@mui/icons-material/Delete';
import { 
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';

// Styled component for the upload area
const UploadArea = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragActive ? theme.palette.primary.light + '20' : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  },
}));

const steps = ['Upload CSV', 'Process Data', 'View Projections'];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`projection-tabpanel-${index}`}
      aria-labelledby={`projection-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `projection-tab-${index}`,
    'aria-controls': `projection-tabpanel-${index}`,
  };
}

function Projections() {
  const { projectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [error, setError] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [recentProjections, setRecentProjections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentProjection, setCurrentProjection] = useState(null);
  const [processingResults, setProcessingResults] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectionToDelete, setProjectionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchRecentProjections();
    
    // If a projectionId is provided, fetch that specific projection
    if (projectionId) {
      fetchProjection(projectionId);
    }
  }, [projectionId]);

  const fetchRecentProjections = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('projections')
        .select('*')
        .order('uploaded_at', { ascending: false })
        .limit(5);
      
      if (error) throw error;
      
      setRecentProjections(data || []);
    } catch (error) {
      console.error('Error fetching recent projections:', error);
      setError('Failed to load recent projections.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjection = async (id) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('projections')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      setCurrentProjection(data);
      
      // If projection is already processed, move to step 2
      if (data.status === 'processed') {
        setActiveStep(2);
      }
      
    } catch (error) {
      console.error('Error fetching projection:', error);
      setError('Failed to load projection details.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const validateAndSetFile = (file) => {
    setError(null);
    // Check file type
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file.');
      return;
    }
    
    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit.');
      return;
    }
    
    setFile(file);
    
    // Read the file content to parse CSV
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const parsedData = parseCSV(content);
      
      if (!parsedData.valid) {
        setError(parsedData.error || 'Invalid CSV format');
        return;
      }
      
      setCsvData(parsedData);
      setActiveStep(1);
    };
    
    reader.onerror = () => {
      setError('Error reading file');
    };
    
    reader.readAsText(file);
  };

  const uploadFile = async () => {
    if (!file || !user || !csvData) return;
    
    try {
      setIsUploading(true);
      setError(null);
      
      // Upload to Supabase storage
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `${user.id}/${fileName}`;
      
      const { error: storageError } = await supabase.storage
        .from('projections')
        .upload(filePath, file);
      
      if (storageError) throw storageError;
      
      // Create record in projections table
      const { data, error: dbError } = await supabase
        .from('projections')
        .insert([
          {
            uploaded_by: user.id,
            file_name: fileName,
            status: 'pending'
          }
        ])
        .select();
      
      if (dbError) throw dbError;
      
      // Get the new projection ID
      const newProjectionId = data[0].id;
      
      // Process the file data
      await processData(csvData.rows, newProjectionId);
      
      // Navigate to the projection detail page
      navigate(`/projections/${newProjectionId}`);
      
    } catch (error) {
      console.error('Error uploading projection file:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const processData = async (rows, projectionId) => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // If no projectionId is provided, create a new projection record first
      let finalProjectionId = projectionId;
      
      if (!finalProjectionId && user) {
        console.log('No projection ID provided. Creating a new projection record...');
        
        // Create a placeholder file name
        const fileName = `Manual_${Date.now()}.csv`;
        
        // Create record in projections table
        const { data, error: dbError } = await supabase
          .from('projections')
          .insert([
            {
              uploaded_by: user.id,
              file_name: fileName,
              status: 'pending'
            }
          ])
          .select();
        
        if (dbError) {
          console.error('Error creating projection record:', dbError);
          throw new Error('Failed to create projection record');
        }
        
        finalProjectionId = data[0].id;
        console.log(`Created new projection with ID: ${finalProjectionId}`);
        
        // Set as current projection
        setCurrentProjection(data[0]);
      }
      
      if (!finalProjectionId) {
        throw new Error('No projection ID available');
      }
      
      const results = await processProjectionData(rows, finalProjectionId);
      
      if (!results.success) {
        throw new Error(results.error || 'Failed to process projection data');
      }
      
      setProcessingResults(results);
      setActiveStep(2);
      await fetchRecentProjections();
      
      // Navigate to the projection detail page
      navigate(`/projections/${finalProjectionId}`);
      
      return results;
    } catch (error) {
      console.error('Error processing projection data:', error);
      setError('Failed to process data: ' + error.message);
      return { success: false, error: error.message };
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleViewProjection = (projectionId) => {
    navigate(`/projections/${projectionId}`);
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleNewProjection = () => {
    // Reset state for new projection
    setActiveStep(0);
    setFile(null);
    setCsvData(null);
    setCurrentProjection(null);
    
    // Navigate to base projections route
    navigate('/projections');
  };

  const handleDeleteClick = (projection) => {
    setProjectionToDelete(projection);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!projectionToDelete) return;
    
    try {
      setIsDeleting(true);
      
      const result = await deleteProjection(projectionToDelete.id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete projection');
      }
      
      // Close the confirmation dialog
      setDeleteConfirmOpen(false);
      
      // Remove projection from state
      setRecentProjections(prev => prev.filter(p => p.id !== projectionToDelete.id));
      
      // If we're viewing the deleted projection, navigate away
      if (projectionId === projectionToDelete.id) {
        navigate('/projections');
      }
      
      // Reset state
      setProjectionToDelete(null);
    } catch (error) {
      console.error('Error deleting projection:', error);
      setError(`Failed to delete projection: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setProjectionToDelete(null);
  };

  const renderUploadSection = () => (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Upload Projection Data
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Upload a CSV file containing future scheduled orders to generate revenue projections.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <input
          type="file"
          id="file-upload"
          onChange={handleFileInput}
          style={{ display: 'none' }}
          accept=".csv"
        />
        
        <UploadArea
          isDragActive={isDragActive}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload').click()}
          sx={{ mb: 3 }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drag & Drop CSV file here
          </Typography>
          <Typography variant="body2" color="textSecondary">
            or click to select file
          </Typography>
          {file && (
            <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
              Selected: {file.name}
            </Typography>
          )}
        </UploadArea>
        
        {csvData && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="success">
              CSV successfully parsed. {csvData.totalCount} rows found.
            </Alert>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={uploadFile}
            disabled={!csvData || isUploading}
            startIcon={isUploading ? <CircularProgress size={24} color="inherit" /> : null}
          >
            {isUploading ? 'Uploading...' : 'Upload & Process'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  const renderProcessingSection = () => (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Processing Data
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {isProcessing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">
              Processing data... Please wait.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Ready to process {csvData?.totalCount || 0} rows of data.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={() => setActiveStep(0)} 
                sx={{ mr: 2 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => processData(csvData.rows)}
                disabled={isProcessing || !csvData}
              >
                Process Data
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );

  const renderProjectionDetailView = () => (
    <Box sx={{ mt: 4 }}>
      {/* Add New Projection button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleNewProjection}
        >
          Create New Projection
        </Button>
      </Box>
      
      {/* Tabs for Details and Estimator */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="projection tabs">
          <Tab label="Projection Details" {...a11yProps(0)} />
          <Tab label="Estimator Tool" {...a11yProps(1)} />
        </Tabs>
      </Box>
      
      <TabPanel value={activeTab} index={0}>
        <ProjectionDetails projectionId={projectionId} />
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <ProjectionEstimator projectionId={projectionId} baseData={currentProjection} />
      </TabPanel>
    </Box>
  );

  const renderRecentProjections = () => (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Recent Projections
        </Typography>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : recentProjections.length === 0 ? (
          <Typography variant="body1">
            No projections found. Upload a CSV file to generate projections.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {recentProjections.map((projection) => (
              <Grid item xs={12} sm={6} md={4} key={projection.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" noWrap title={projection.file_name}>
                      {projection.file_name.split('_').slice(1).join('_')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Uploaded: {formatDate(projection.uploaded_at)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Status: {projection.status}
                    </Typography>
                    {projection.status === 'processed' && (
                      <>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Locations: {projection.total_locations || 0}
                        </Typography>
                        <Typography variant="body2">
                          Projected Revenue: {formatCurrency(projection.total_projected_revenue)}
                        </Typography>
                        <Typography variant="body2">
                          Projected Gross Profit: {formatCurrency(projection.total_projected_margin)}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      color="primary"
                      onClick={() => handleViewProjection(projection.id)}
                    >
                      View Details
                    </Button>
                    <Button 
                      size="small" 
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(projection)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );

  // Add the confirmation dialog
  const renderDeleteConfirmationDialog = () => (
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleDeleteCancel}
      aria-labelledby="delete-projection-dialog-title"
      aria-describedby="delete-projection-dialog-description"
    >
      <DialogTitle id="delete-projection-dialog-title">
        Delete Projection
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="delete-projection-dialog-description">
          Are you sure you want to delete the projection 
          {projectionToDelete ? ` "${projectionToDelete.file_name.split('_').slice(1).join('_')}"` : ''}?
          This action cannot be undone and will permanently delete all associated data.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteConfirm} 
          color="error" 
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Main render
  // If we're viewing a specific projection
  if (projectionId && currentProjection) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Projections & Estimator
        </Typography>
        {renderProjectionDetailView()}
        {renderDeleteConfirmationDialog()}
      </Box>
    );
  }

  // Otherwise show the upload flow
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Projections & Estimator
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {activeStep === 0 && renderUploadSection()}
      {activeStep === 1 && renderProcessingSection()}
      {renderRecentProjections()}
      {renderDeleteConfirmationDialog()}
    </Box>
  );
}

export default Projections; 